"use client";

import type { CSSProperties } from "react";
import { motion } from "framer-motion";
import { PortableText } from "next-sanity";

import {
  REVIEW_URLS as TRUSTPILOT_REVIEW_URLS,
  TrustpilotMicroTrustScore,
} from "@/components/global/TrustpilotWidgets";
import { GroqImage } from "@/helpers/Image";
import { IFinalCta } from "@/sanity/lib/definitions";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";

const ease = [0.25, 0.1, 0.25, 1] as const;

const MOBILE_IMAGE_WIDTH_REM = 69.57;
const MOBILE_IMAGE_STRIP_REM = 56.88;

const TRUSTPILOT_FRAME = {
  "en-US": { pull: "lg:-ml-[83px]", link: "inset-x-[83px]", button: "lg:min-w-[234px]" },
  "de-DE": { pull: "lg:-ml-[73px]", link: "inset-x-[73px]", button: "lg:min-w-[254px]" },
} as const;

export default function FooterCta({
  data,
  language,
  showTrustpilot = true,
  wideTitle = false,
}: {
  data: IFinalCta;
  language: string;
  showTrustpilot?: boolean;
  wideTitle?: boolean;
}) {
  if (!data.finalCtaTitle && !data.finalCtaImage) return null;

  const trustpilotLocale = language === "de" ? "de-DE" : "en-US";
  const trustpilotFrame = TRUSTPILOT_FRAME[trustpilotLocale];
  const titleWidthClass = !wideTitle
    ? "lg:w-[75.75rem]"
    : language === "de" ? "lg:w-[95rem]" : "lg:w-[87.25rem]";

  const image = data.finalCtaImage;
  const mobileImageHeight =
    image?.width && image?.height
      ? (MOBILE_IMAGE_WIDTH_REM * image.height) / image.width
      : MOBILE_IMAGE_STRIP_REM;

  return (
    <section className="relative w-full overflow-clip bg-[#04271d]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[url('/footer/cta-texture.webp')] bg-[length:120.3rem_123.8rem] bg-repeat opacity-20 mix-blend-exclusion"
      />

      <div
        className={`relative mx-auto flex flex-col max-lg:w-full max-lg:px-[2.5rem] max-lg:pt-[6rem] lg:w-[176.5rem] lg:py-[17.5rem] ${
          image ? "" : "max-lg:pb-[6rem]"
        }`}
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.8, ease }}
          className="relative z-10 flex flex-col gap-[4rem] max-lg:items-center lg:items-start"
        >
          <div className="flex flex-col gap-[1rem] text-white max-lg:w-full max-lg:items-center lg:items-start">
            {data.finalCtaChip && (
              <p className="mb-0 font-blacker text-[2rem] leading-[1.5] italic text-white max-lg:text-center">
                {data.finalCtaChip}
              </p>
            )}

            {data.finalCtaTitle && (
              <div className={`richtext prose-headings:m-0 prose-headings:font-normal prose-headings:leading-[1.05] prose-headings:text-white [&_p]:m-0 [&_p]:font-normal [&_p]:leading-[1.05] [&_p]:text-white max-lg:w-full max-lg:text-center max-lg:[text-wrap:balance] max-lg:prose-headings:text-[4rem] max-lg:prose-headings:tracking-[-0.16rem] max-lg:[&_p]:text-[4rem] max-lg:[&_p]:tracking-[-0.16rem] lg:richtext-start lg:text-left lg:prose-headings:text-[7rem] lg:prose-headings:tracking-[-0.28rem] lg:[&_p]:text-[7rem] lg:[&_p]:tracking-[-0.28rem] ${titleWidthClass}`}>
                <PortableText
                  value={data.finalCtaTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {(data.finalCtaButton || showTrustpilot) && (
            <div className="flex w-full flex-col gap-[3rem] max-lg:items-center lg:items-start">
              {data.finalCtaButton && (
                <div
                  className={`max-lg:w-full lg:w-fit [&_.button]:min-h-[5.75rem] [&_.button]:rounded-[0.5rem] [&_.button]:text-[1.75rem] [&_.button]:tracking-[-0.035rem] ${
                    showTrustpilot ? trustpilotFrame.button : ""
                  }`}
                >
                  <CTAButton data={data.finalCtaButton} overrideVariant="white" />
                </div>
              )}

              {showTrustpilot && (
                <div
                  className={`relative w-[400px] shrink-0 [&_iframe]:pointer-events-none ${trustpilotFrame.pull}`}
                >
                  <TrustpilotMicroTrustScore locale={trustpilotLocale} />
                  <a
                    href={TRUSTPILOT_REVIEW_URLS[trustpilotLocale]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="IQ Capital on Trustpilot"
                    className={`absolute inset-y-0 rounded-[0.25rem] outline-none focus-visible:ring-1 focus-visible:ring-white/40 ${trustpilotFrame.link}`}
                  />
                </div>
              )}
            </div>
          )}
        </motion.div>

        {image && (
          <motion.div
            initial={{ opacity: 0, x: 28, scale: 0.98 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, ease, delay: 0.12 }}
            style={{ "--cta-image-h": `${mobileImageHeight}rem` } as CSSProperties}
            className="pointer-events-none max-lg:relative max-lg:-mx-[2.5rem] max-lg:-mt-[2.4rem] max-lg:h-[min(56.88rem,var(--cta-image-h))] max-lg:overflow-hidden lg:absolute lg:left-[87.25rem] lg:top-0 lg:h-full lg:w-[90.25rem]"
          >
            <GroqImage
              src={image.src}
              alt={image.name || ""}
              width={image.width}
              height={image.height}
              quality={100}
              sizes="(min-width: 993px) 48vw, 160vw"
              className="max-w-none max-lg:absolute max-lg:left-1/2 max-lg:top-0 max-lg:!h-auto max-lg:!w-[69.57rem] max-lg:-translate-x-1/2 lg:!h-full lg:!w-full lg:object-cover lg:object-top"
            />
          </motion.div>
        )}
      </div>
    </section>
  );
}
